/**
 * Reports V2 State Management
 *
 * Manages:
 * - Time range selection (preset or custom)
 * - Comparison period configuration
 * - UI state (loading, errors)
 */

import { DateTime } from "luxon";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { useSalonStore } from "@/lib/store/salon";

import {
  ComparisonStrategy,
  SpecificMonth,
  TimePeriod,
  TimeRangeConfig,
  TimeRangePreset,
} from "../types";

export type DiscountAnalyticsScope = "SALON" | "FRANCHISE";

interface ReportsV2State {
  // Time range configuration
  timeRange: TimeRangeConfig;

  // Calculated periods (derived from timeRange)
  currentPeriod: TimePeriod | null;
  comparisonPeriod: TimePeriod | null;

  // Discounts tab scope (defaults to SALON; FRANCHISE shown only to OWNER actors)
  discountScope: DiscountAnalyticsScope;
  selectedDiscountId: string | null;

  // Actions
  setTimeRangePreset: (preset: TimeRangePreset) => void;
  setCustomTimeRange: (startDate: Date, endDate: Date) => void;
  setSpecificMonth: (year: number, month: number) => void;
  setComparisonStrategy: (strategy: ComparisonStrategy) => void;
  setCustomComparisonRange: (startDate: Date, endDate: Date) => void;
  setDiscountScope: (scope: DiscountAnalyticsScope) => void;
  setSelectedDiscountId: (discountId: string | null) => void;

  // Internal helpers
  calculatePeriods: () => void;
}

const getReportsTimezone = (): string => {
  return useSalonStore.getState().currentSalon?.timezone || "UTC";
};

/**
 * Calculate time period from preset. For SPECIFIC_MONTH, the caller must
 * pass `specificMonth` — without it the preset has no concrete range.
 */
const calculatePeriodFromPreset = (
  preset: TimeRangePreset,
  specificMonth?: SpecificMonth
): TimePeriod | null => {
  const timezone = getReportsTimezone();
  const now = DateTime.now().setZone(timezone);

  switch (preset) {
    case "LAST_7_DAYS": {
      const endDate = now.endOf("day");
      const startDate = endDate.minus({ days: 6 }).startOf("day");
      return {
        startDate: startDate.toJSDate(),
        endDate: endDate.toJSDate(),
        label: "Last 7 days",
      };
    }

    case "LAST_30_DAYS": {
      const endDate = now.endOf("day");
      const startDate = endDate.minus({ days: 29 }).startOf("day");
      return {
        startDate: startDate.toJSDate(),
        endDate: endDate.toJSDate(),
        label: "Last 30 days",
      };
    }

    case "LAST_90_DAYS": {
      const endDate = now.endOf("day");
      const startDate = endDate.minus({ days: 89 }).startOf("day");
      return {
        startDate: startDate.toJSDate(),
        endDate: endDate.toJSDate(),
        label: "Last 90 days",
      };
    }

    case "SPECIFIC_MONTH": {
      if (!specificMonth) return null;
      const monthStart = DateTime.fromObject(
        {
          year: specificMonth.year,
          month: specificMonth.month,
          day: 1,
        },
        { zone: timezone }
      ).startOf("month");
      const monthEnd = monthStart.endOf("month");
      return {
        startDate: monthStart.toJSDate(),
        endDate: monthEnd.toJSDate(),
        label: monthStart.toFormat("MMM yyyy"),
      };
    }

    case "LAST_WEEK": {
      const lastWeekStart = now.minus({ weeks: 1 }).startOf("week");
      const lastWeekEnd = lastWeekStart.endOf("week");
      return {
        startDate: lastWeekStart.toJSDate(),
        endDate: lastWeekEnd.toJSDate(),
        label: `Last Week (${lastWeekStart.toFormat("MMM d")} - ${lastWeekEnd.toFormat("MMM d")})`,
      };
    }

    case "LAST_MONTH": {
      const lastMonthStart = now.minus({ months: 1 }).startOf("month");
      const lastMonthEnd = lastMonthStart.endOf("month");
      return {
        startDate: lastMonthStart.toJSDate(),
        endDate: lastMonthEnd.toJSDate(),
        label: `Last Month (${lastMonthStart.toFormat("MMM yyyy")})`,
      };
    }

    case "LAST_QUARTER": {
      const lastQuarterStart = now.minus({ quarters: 1 }).startOf("quarter");
      const lastQuarterEnd = lastQuarterStart.endOf("quarter");
      return {
        startDate: lastQuarterStart.toJSDate(),
        endDate: lastQuarterEnd.toJSDate(),
        label: `Last Quarter (Q${lastQuarterStart.quarter} ${lastQuarterStart.year})`,
      };
    }

    case "LAST_YEAR": {
      const lastYearStart = now.minus({ years: 1 }).startOf("year");
      const lastYearEnd = lastYearStart.endOf("year");
      return {
        startDate: lastYearStart.toJSDate(),
        endDate: lastYearEnd.toJSDate(),
        label: `Last Year (${lastYearStart.year})`,
      };
    }

    default:
      return null;
  }
};

/**
 * Calculate comparison period based on strategy
 */
const calculateComparisonPeriod = (
  currentPeriod: TimePeriod,
  preset: TimeRangePreset,
  strategy: ComparisonStrategy,
  customComparisonRange?: TimePeriod
): TimePeriod | null => {
  if (strategy === ComparisonStrategy.CUSTOM && customComparisonRange) {
    return customComparisonRange;
  }

  const timezone = getReportsTimezone();
  const startDate = DateTime.fromJSDate(currentPeriod.startDate, { zone: timezone }).startOf("day");
  const endDate = DateTime.fromJSDate(currentPeriod.endDate, { zone: timezone }).startOf("day");

  // Calendar-aligned presets get a calendar-aligned comparison — e.g.
  // LAST_MONTH (April) compares to the whole previous month (March 1
  // → March 31), not "duration-days before April". Otherwise the user
  // sees ranges like "2/3 → 31/3" because April only has 30 days.
  const calendarUnitByPreset: Partial<
    Record<TimeRangePreset, "week" | "month" | "quarter" | "year">
  > = {
    [TimeRangePreset.LAST_WEEK]: "week",
    [TimeRangePreset.LAST_MONTH]: "month",
    [TimeRangePreset.SPECIFIC_MONTH]: "month",
    [TimeRangePreset.LAST_QUARTER]: "quarter",
    [TimeRangePreset.LAST_YEAR]: "year",
  };
  const calendarUnit = calendarUnitByPreset[preset];

  if (strategy === ComparisonStrategy.PREVIOUS_PERIOD) {
    if (calendarUnit) {
      const comparisonStart = startDate.minus({ [`${calendarUnit}s`]: 1 }).startOf(calendarUnit);
      const comparisonEnd = comparisonStart.endOf(calendarUnit);
      return {
        startDate: comparisonStart.toJSDate(),
        endDate: comparisonEnd.toJSDate(),
        label: `${comparisonStart.toFormat("dd/MM")} - ${comparisonEnd.toFormat("dd/MM/yyyy")}`,
      };
    }

    // Sliding-window presets (LAST_7_DAYS, LAST_30_DAYS, LAST_90_DAYS,
    // CUSTOM) — comparison is the immediately preceding stretch of the
    // same number of days.
    const durationInDays = Math.round(endDate.diff(startDate, "days").days) + 1;
    const comparisonEnd = startDate.minus({ days: 1 });
    const comparisonStart = comparisonEnd.minus({ days: durationInDays - 1 });

    return {
      startDate: comparisonStart.toJSDate(),
      endDate: comparisonEnd.endOf("day").toJSDate(),
      label: `${comparisonStart.toFormat("dd/MM")} - ${comparisonEnd.toFormat("dd/MM/yyyy")}`,
    };
  }

  if (strategy === ComparisonStrategy.SAME_PERIOD_LAST_YEAR) {
    // Year-over-year comparison
    const comparisonStart = startDate.minus({ years: 1 });
    const comparisonEnd = endDate.minus({ years: 1 });

    return {
      startDate: comparisonStart.toJSDate(),
      endDate: comparisonEnd.endOf("day").toJSDate(),
      label: `${comparisonStart.toFormat("dd/MM")} - ${comparisonEnd.toFormat("dd/MM/yyyy")}`,
    };
  }

  return null;
};

/**
 * Get default comparison strategy for preset
 */
const getDefaultComparisonStrategy = (_preset: TimeRangePreset): ComparisonStrategy => {
  // All periods use previous period comparison by default
  return ComparisonStrategy.PREVIOUS_PERIOD;
};

export const useReportsV2Store = create<ReportsV2State>()(
  devtools(
    (set, get) => {
      // Calculate initial periods
      const initialCurrentPeriod = calculatePeriodFromPreset(TimeRangePreset.LAST_MONTH);
      const initialComparisonPeriod = initialCurrentPeriod
        ? calculateComparisonPeriod(
            initialCurrentPeriod,
            TimeRangePreset.LAST_MONTH,
            ComparisonStrategy.PREVIOUS_PERIOD
          )
        : null;

      return {
        // Initial state - Last Month with previous period comparison
        timeRange: {
          preset: TimeRangePreset.LAST_MONTH,
          comparisonStrategy: ComparisonStrategy.PREVIOUS_PERIOD,
        },
        currentPeriod: initialCurrentPeriod,
        comparisonPeriod: initialComparisonPeriod,
        discountScope: "SALON",
        selectedDiscountId: null,

        setDiscountScope: (scope) => {
          set({ discountScope: scope, selectedDiscountId: null });
        },

        setSelectedDiscountId: (discountId) => {
          set({ selectedDiscountId: discountId });
        },

        setTimeRangePreset: (preset) => {
          const defaultStrategy = getDefaultComparisonStrategy(preset);
          set({
            timeRange: {
              preset,
              comparisonStrategy: defaultStrategy,
            },
          });
          get().calculatePeriods();
        },

        setCustomTimeRange: (startDate, endDate) => {
          const label = `${DateTime.fromJSDate(startDate).toFormat("dd/MM")} - ${DateTime.fromJSDate(endDate).toFormat("dd/MM/yyyy")}`;
          set({
            timeRange: {
              preset: TimeRangePreset.CUSTOM,
              customRange: { startDate, endDate, label },
              comparisonStrategy: ComparisonStrategy.PREVIOUS_PERIOD,
            },
          });
          get().calculatePeriods();
        },

        setSpecificMonth: (year, month) => {
          set({
            timeRange: {
              preset: TimeRangePreset.SPECIFIC_MONTH,
              specificMonth: { year, month },
              comparisonStrategy: ComparisonStrategy.PREVIOUS_PERIOD,
            },
          });
          get().calculatePeriods();
        },

        setComparisonStrategy: (strategy) => {
          set((state) => ({
            timeRange: {
              ...state.timeRange,
              comparisonStrategy: strategy,
            },
          }));
          get().calculatePeriods();
        },

        setCustomComparisonRange: (startDate, endDate) => {
          const label = `${DateTime.fromJSDate(startDate).toFormat("dd/MM")} - ${DateTime.fromJSDate(endDate).toFormat("dd/MM/yyyy")}`;
          set((state) => ({
            timeRange: {
              ...state.timeRange,
              comparisonStrategy: ComparisonStrategy.CUSTOM,
              customComparisonRange: { startDate, endDate, label },
            },
          }));
          get().calculatePeriods();
        },

        calculatePeriods: () => {
          const { timeRange } = get();

          // Calculate current period
          let currentPeriod: TimePeriod | null = null;
          if (timeRange.preset === TimeRangePreset.CUSTOM && timeRange.customRange) {
            currentPeriod = timeRange.customRange;
          } else {
            currentPeriod = calculatePeriodFromPreset(timeRange.preset, timeRange.specificMonth);
          }

          // Calculate comparison period
          let comparisonPeriod: TimePeriod | null = null;
          if (currentPeriod) {
            comparisonPeriod = calculateComparisonPeriod(
              currentPeriod,
              timeRange.preset,
              timeRange.comparisonStrategy,
              timeRange.customComparisonRange
            );
          }

          set({ currentPeriod, comparisonPeriod });
        },
      };
    },
    { name: "reports-v2-store" }
  )
);

// The initial period values are computed at module-load — BEFORE the user
// has selected a salon, so they fall back to the browser's timezone. Once
// the salon is picked, `TimezoneService.setSalonTimezone` updates Luxon's
// default zone, but the already-computed periods still reflect the
// browser TZ. Subscribing to salon-store changes lets us recompute the
// periods in the salon's timezone the moment the salon (and therefore
// timezone) becomes known, and again on every salon switch.
useSalonStore.subscribe((state, prev) => {
  const nextTz = state.currentSalon?.timezone;
  const prevTz = prev.currentSalon?.timezone;
  if (nextTz && nextTz !== prevTz) {
    useReportsV2Store.getState().calculatePeriods();
  }
});
